import React, { useEffect, useState } from 'react';
import SideModal from 'src/components/SideModal';
import ModalHeader from 'src/components/ModalHeader';
import ModalFooter from 'src/components/ModalFooter';
import Input, { Label } from 'src/components/Input';
import Flex from 'src/components/Flex';
import AiPromptDropdown from 'src/components/AiPromptDropdown';
import { useCreatePromptContext, useUpdatePromptContext, AiPromptContext } from 'src/queries/useAiPromptContexts';
import { usePrompts, AiPrompt } from 'src/queries/useAiPrompts';
import { ToastStoreObject, ToastType } from 'src/stores/ToastStore';
import { parseError } from 'src/utils';
import styles from './styles.module.scss';

interface EditAiPromptContextModalProps {
    isOpen: boolean;
    onClose: () => void;
    promptContext?: AiPromptContext | null;
    mode: 'create' | 'edit';
    facilityId?: number;
}

function EditAiPromptContextModal({ isOpen, onClose, promptContext, mode, facilityId }: EditAiPromptContextModalProps) {
    const createPromptContextMutation = useCreatePromptContext();
    const updatePromptContextMutation = useUpdatePromptContext();
    const { data: prompts = [] } = usePrompts();

    const [promptContextData, setPromptContextData] = useState({
        aiPromptId: '',
        facilityId,
        aiPromptContextText: '',
    });
    const [selectedPrompt, setSelectedPrompt] = useState<AiPrompt | null>(null);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    const isLoading = createPromptContextMutation.isLoading || updatePromptContextMutation.isLoading;

    useEffect(() => {
        if (isOpen) {
            if (mode === 'edit' && promptContext) {
                const { aiPromptId, facilityId, aiPromptContextText } = promptContext;
                const prompt = prompts.find((p: AiPrompt) => p.promptId === aiPromptId);
                setPromptContextData({
                    aiPromptId,
                    facilityId,
                    aiPromptContextText: aiPromptContextText || '',
                });
                setSelectedPrompt(prompt || null);
            } else {
                setPromptContextData({
                    aiPromptId: '',
                    facilityId,
                    aiPromptContextText: '',
                });
                setSelectedPrompt(null);
            }
            setErrors({});
        }
    }, [isOpen, mode, promptContext, facilityId, prompts]);

    const validateForm = () => {
        const newErrors: { [key: string]: string } = {};

        if (!promptContextData.facilityId || promptContextData.facilityId === 0) {
            newErrors.facilityId = 'No facility selected. Please select a facility first.';
        }

        if (!promptContextData.aiPromptId) {
            newErrors.aiPromptId = 'AI Prompt is required';
        }

        if (!promptContextData.aiPromptContextText.trim()) {
            newErrors.aiPromptContextText = 'Context text is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        if (mode === 'edit' && !promptContext) {
            ToastStoreObject.show('Error: No prompt context selected for editing', ToastType.Error);
            return;
        }

        try {
            if (mode === 'create') {
                await createPromptContextMutation.mutateAsync(promptContextData);
                ToastStoreObject.show('Prompt context created successfully', ToastType.Success);
            } else {
                await updatePromptContextMutation.mutateAsync({
                    promptContextId: promptContext!.aiPromptContextId,
                    promptContextData,
                });
                ToastStoreObject.show('Prompt context updated successfully', ToastType.Success);
            }
            onClose();
        } catch (error) {
            const errorMessage = parseError(error);
            ToastStoreObject.show(errorMessage, ToastType.Error);
        }
    };

    const handleInputChange = (field: string, value: string) => {
        setPromptContextData((prev) => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors((prev) => ({ ...prev, [field]: '' }));
        }
    };

    const handlePromptChange = (prompt: AiPrompt | null) => {
        setSelectedPrompt(prompt);
        setPromptContextData((prev) => ({
            ...prev,
            aiPromptId: prompt?.promptId || '',
        }));
        if (errors.aiPromptId) {
            setErrors((prev) => ({ ...prev, aiPromptId: '' }));
        }
    };

    const getPromptName = (promptId: string) => {
        const prompt = prompts.find((p: AiPrompt) => p.promptId === promptId);
        return prompt ? prompt.promptName : 'Unknown Prompt';
    };

    return (
        <SideModal isOpen={isOpen} onClose={onClose}>
            <ModalHeader
                title={mode === 'create' ? 'Create Prompt Context' : 'Edit Prompt Context'}
                onClose={onClose}
            />

            <div className={styles.modalContent}>
                <Flex direction="column" className={styles.formContainer}>
                    {mode === 'edit' && (
                        <Input
                            label="Prompt Name"
                            value={getPromptName(promptContextData.aiPromptId)}
                            disabled
                            placeholder="Prompt name"
                        />
                    )}

                    {mode === 'create' && (
                        <div>
                            <Label text="AI Prompt" required />
                            <AiPromptDropdown
                                value={selectedPrompt}
                                onChange={handlePromptChange}
                                placeholder="Select a prompt"
                                isClearable={false}
                                infoState={errors.aiPromptId ? 'error' : undefined}
                            />
                        </div>
                    )}
                    <Input
                        label="Context Text"
                        value={promptContextData.aiPromptContextText}
                        onChangeText={(value: string) => handleInputChange('aiPromptContextText', value)}
                        placeholder="Enter the context text for this prompt"
                        multiLine
                        rows={25}
                        errorMessage={errors.aiPromptContextText}
                        required
                    />
                </Flex>
            </div>

            <ModalFooter
                primaryText={mode === 'create' ? 'Create' : 'Update'}
                secondaryText="Cancel"
                primaryClick={handleSubmit}
                secondaryClick={onClose}
                isLoading={isLoading}
            />
        </SideModal>
    );
}

export default EditAiPromptContextModal;
